import { useRef, useState } from "react";
import { ArrowLeft, Camera, Loader2 } from "lucide-react";
import { compressImageForUpload } from "@/lib/driverImageUpload";
import { registerDeliveryPartner } from "@/lib/driverPartner";

const PINK = "#EE2677";

/** Partner sign-up form — mandatory before using the Delivery app. */
export default function DriverPartnerOnboard({
  onComplete,
  onCancel,
  mandatory = false,
  initialPhone = "",
  phoneLocked = false,
  firebaseIdToken = "",
}) {
  const fileRef = useRef(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState(initialPhone);
  const [upiId, setUpiId] = useState("");
  const [address, setAddress] = useState("");
  const [scooterNumber, setScooterNumber] = useState("");
  const [payoutKind, setPayoutKind] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onPickPhoto = async (file) => {
    if (!file) return;
    try {
      const { blob } = await compressImageForUpload(file, { maxEdge: 640, webpQuality: 0.72, jpegQuality: 0.72 });
      setPhotoFile(blob);
      setPhotoPreview(URL.createObjectURL(blob));
      setErr("");
    } catch (e) {
      setErr(e.message || "Could not process photo");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!photoFile) {
      setErr("Add your profile photo");
      return;
    }
    if (!payoutKind) {
      setErr("Choose Rider or Team partner.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const data = await registerDeliveryPartner({
        name,
        phone,
        address,
        scooterNumber,
        upiId,
        photoFile,
        firebaseIdToken,
        payoutKind,
      });
      onComplete?.(data);
    } catch (ex) {
      setErr(ex.message || String(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="da-partner-onboard">
      <style>{`
        .da-partner-onboard {
          min-height: 100dvh;
          background: #F5F2EA;
          color: #2D2D2D;
          padding: 12px 14px 28px;
          max-width: 480px;
          margin: 0 auto;
          font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
        }
        .da-partner-onboard * { box-sizing: border-box; }
        .da-partner-head {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }
        .da-partner-back {
          border: none;
          background: #fff;
          border-radius: 12px;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 1px 4px rgba(0,0,0,0.08);
        }
        .da-partner-title { font-size: 20px; font-weight: 800; margin: 0; }
        .da-partner-sub { font-size: 14px; color: rgba(45,45,45,0.65); margin: 6px 0 0; line-height: 1.45; }
        .da-partner-field { margin-bottom: 12px; }
        .da-partner-field label {
          display: block;
          font-size: 13px;
          font-weight: 700;
          margin-bottom: 6px;
        }
        .da-partner-field input, .da-partner-field textarea {
          width: 100%;
          border: 2px solid #E5E7EB;
          border-radius: 14px;
          padding: 12px 14px;
          font-size: 16px;
          background: #fff;
        }
        .da-partner-field textarea { min-height: 72px; resize: vertical; }
        .da-partner-photo-row {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .da-partner-photo-preview {
          width: 72px;
          height: 72px;
          border-radius: 16px;
          object-fit: cover;
          border: 2px solid ${PINK};
          background: #fff;
        }
        .da-partner-photo-btn {
          flex: 1;
          border: 2px dashed #D1D5DB;
          border-radius: 14px;
          padding: 14px;
          background: #fff;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .da-partner-primary {
          width: 100%;
          border: none;
          border-radius: 16px;
          padding: 16px;
          font-size: 17px;
          font-weight: 800;
          color: #fff;
          background: ${PINK};
          cursor: pointer;
          margin-top: 8px;
        }
        .da-partner-primary:disabled { opacity: 0.6; cursor: wait; }
        .da-partner-err { color: #B91C1C; font-size: 14px; font-weight: 600; margin: 8px 0; }
        .da-partner-bonus {
          background: linear-gradient(135deg, #FFF0F6, #FEF3C7);
          border: 2px solid #F9A8D4;
          border-radius: 16px;
          padding: 14px;
          margin-bottom: 16px;
          font-weight: 700;
          text-align: center;
        }
        .da-partner-brand {
          text-align: center;
          margin-bottom: 18px;
        }
        .da-partner-brand-kicker {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(45,45,45,0.45);
          margin: 0 0 4px;
        }
        .da-partner-brand-name {
          font-size: 28px;
          font-weight: 900;
          color: ${PINK};
          margin: 0;
          line-height: 1.1;
        }
        .da-partner-kind-btn {
          border: 2px solid #E5E7EB;
          border-radius: 14px;
          padding: 12px 10px;
          background: #fff;
          font-size: 15px;
          font-weight: 900;
          cursor: pointer;
          text-align: left;
          color: #2D2D2D;
        }
        .da-partner-kind-btn--active.da-partner-kind-btn--rider {
          border-color: ${PINK};
          background: ${PINK};
          color: white;
        }
        .da-partner-kind-btn--active.da-partner-kind-btn--internal {
          border-color: #6366F1;
          background: #6366F1;
          color: white;
        }
      `}</style>

      {mandatory ? (
        <div className="da-partner-brand">
          <p className="da-partner-brand-kicker">Delivery app</p>
          <h2 className="da-partner-brand-name">The Table Top</h2>
        </div>
      ) : null}

      <div className="da-partner-head">
        {onCancel ? (
          <button type="button" className="da-partner-back" onClick={onCancel} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
        ) : null}
        <div>
          <h1 className="da-partner-title">Delivery partner sign-up</h1>
          <p className="da-partner-sub">
            {mandatory
              ? "Fill in your details below to start — name, photo, phone, address & scooter number."
              : "Name, photo, phone, address & scooter number — then you're in."}
          </p>
        </div>
      </div>

      <form onSubmit={submit}>
        <div className="da-partner-field">
          <label>I am signing up as</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <button
              type="button"
              className={`da-partner-kind-btn da-partner-kind-btn--rider ${payoutKind === "rider" ? "da-partner-kind-btn--active" : ""}`}
              onClick={() => { setPayoutKind("rider"); setErr(""); }}
            >
              Rider
              <span style={{ display: "block", fontSize: 11, fontWeight: 700, opacity: 0.85, marginTop: 4 }}>
                Outbound — Table Top pays per trip
              </span>
            </button>
            <button
              type="button"
              className={`da-partner-kind-btn da-partner-kind-btn--internal ${payoutKind === "internal" ? "da-partner-kind-btn--active" : ""}`}
              onClick={() => { setPayoutKind("internal"); setErr(""); }}
            >
              Team partner
              <span style={{ display: "block", fontSize: 11, fontWeight: 700, opacity: 0.85, marginTop: 4 }}>
                Table Top staff — no trip pay
              </span>
            </button>
          </div>
        </div>
        <div className="da-partner-field">
          <label htmlFor="dp-name">Full name</label>
          <input id="dp-name" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
        </div>
        <div className="da-partner-field">
          <label htmlFor="dp-phone">Mobile number</label>
          <input
            id="dp-phone"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            readOnly={phoneLocked}
            required
            autoComplete="tel"
            placeholder="10-digit mobile"
          />
        </div>
        <div className="da-partner-field">
          <label htmlFor="dp-upi">Your UPI ID</label>
          <input
            id="dp-upi"
            value={upiId}
            onChange={(e) => setUpiId(e.target.value.trim())}
            required
            autoComplete="off"
            placeholder="name@paytm"
            inputMode="email"
          />
        </div>
        <div className="da-partner-field">
          <label htmlFor="dp-address">Home address</label>
          <textarea id="dp-address" value={address} onChange={(e) => setAddress(e.target.value)} required />
        </div>
        <div className="da-partner-field">
          <label htmlFor="dp-scooter">Scooter number plate</label>
          <input
            id="dp-scooter"
            value={scooterNumber}
            onChange={(e) => setScooterNumber(e.target.value.toUpperCase())}
            required
            placeholder="DL01AB1234"
          />
        </div>
        <div className="da-partner-field">
          <label>Your photo</label>
          <div className="da-partner-photo-row">
            {photoPreview ? (
              <img src={photoPreview} alt="" className="da-partner-photo-preview" />
            ) : (
              <div
                className="da-partner-photo-preview"
                style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <Camera size={24} color={PINK} />
              </div>
            )}
            <button type="button" className="da-partner-photo-btn" onClick={() => fileRef.current?.click()}>
              <Camera size={18} />
              {photoPreview ? "Retake photo" : "Take profile photo"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="user"
              style={{ display: "none" }}
              onChange={(e) => onPickPhoto(e.target.files?.[0])}
            />
          </div>
        </div>
        {err ? <p className="da-partner-err">{err}</p> : null}
        <button type="submit" className="da-partner-primary" disabled={busy}>
          {busy ? <Loader2 className="animate-spin" size={18} style={{ verticalAlign: "middle" }} /> : null}
          {busy ? " Creating account…" : "Sign up & start deliveries"}
        </button>
      </form>
    </div>
  );
}
